import { SelectedJobState } from './../reducers/jobs.reducer';
import { TranslateService } from '@ngx-translate/core';
import { ContentService } from './../../lib/services/contentService';
import { FacebookService, IntercomService } from '../../lib/services';
import { Router } from '@angular/router';
import { Actions, Effect, ofType } from '@ngrx/effects';
import { Observable } from 'rxjs';
import { Action } from '@ngrx/store';
import {
  catchError,
  map,
  mergeMap,
  switchMap,
  take,
  tap,
  withLatestFrom,
} from 'rxjs/operators';
import { Injectable } from '@angular/core';
import * as jobsActions from '../actions/jobs.actions';
import * as listingActions from '../actions/listing.actions';
import * as processActions from '../edit-job-tpl-process/actions/process.actions';
import { JobsService } from '../services/job.service';
import { Job } from '../models/job.model';
import { EMPTY_LISTING } from '../models/listing.model';
import { MatSnackBar } from '@angular/material';
import { Assignment } from '../models/assignment.model';
import { environment } from '../../../environments/environment';
import { Store } from '@ngrx/store';
import * as fromRoot from '../reducers';
import { JobPostCanDeactivateGuard } from '../../lib/services/routeGuard';
import { EMPTY, of } from 'rxjs';

@Injectable()
export class JobsEffects {
  @Effect()
  LoadJobs$: Observable<Action> = this.actions$.pipe(
    ofType<jobsActions.LoadJobs>(jobsActions.JobsActionTypes.LoadJobs),
    switchMap(action =>
      this.jobsService
        .getJobs(
          action.payload.unit_id,
          action.payload.is_draft,
          action.payload.is_published,
          action.payload.is_template
        )
        .pipe(
          map(
            (response: any) =>
              new jobsActions.LoadJobsSuccess({
                jobs: response.results,
              })
          ),
          catchError(errors =>
            of(new jobsActions.LoadingJobsFailed({ errors }))
          )
        )
    )
  );

  @Effect()
  LoadJob$: Observable<Action> = this.actions$.pipe(
    ofType<jobsActions.LoadJob>(jobsActions.JobsActionTypes.LoadJob),
    switchMap(action =>
      this.jobsService.getJob(action.payload.jobId).pipe(
        map(response => new jobsActions.LoadJobSuccess({ job: response })),
        catchError(errors => of(new jobsActions.LoadingJobsFailed({ errors })))
      )
    )
  );

  @Effect()
  LoadJobCategories$: Observable<Action> = this.actions$.pipe(
    ofType<jobsActions.LoadJobCategories>(
      jobsActions.JobsActionTypes.LoadJobCategories
    ),
    switchMap(action =>
      this.jobsService.getJobCategories().pipe(
        map(
          (response: any) =>
            new jobsActions.LoadJobCategoriesSuccess({
              categories: response.results,
            })
        ),
        catchError(errors => of(new jobsActions.LoadingJobsFailed({ errors })))
      )
    )
  );

  @Effect()
  LoadJobTitleSuggestions$: Observable<Action> = this.actions$.pipe(
    ofType<jobsActions.LoadJobTitleSuggestions>(
      jobsActions.JobsActionTypes.LoadJobTitleSuggestions
    ),
    switchMap(action =>
      this.jobsService.getJobTitleSuggestions(action.payload.term).pipe(
        map(
          (response: any) =>
            new jobsActions.LoadJobTitleSuggestionsSuccess({
              jobTitleSuggestions: response.results,
            })
        ),
        catchError(errors => of(new jobsActions.LoadingJobsFailed({ errors })))
      )
    )
  );

  @Effect()
  LoadJobAssignments$: Observable<Action> = this.actions$.pipe(
    ofType<jobsActions.LoadJobAssignments>(
      jobsActions.JobsActionTypes.LoadJobAssignments
    ),
    switchMap(action =>
      this.jobsService.getJobAssignments(action.payload.jobId).pipe(
        map(
          (response: any) =>
            new jobsActions.LoadJobAssignmentsSuccess({
              jobAssignments: response.results,
            })
        ),
        catchError(errors => of(new jobsActions.LoadingJobsFailed({ errors })))
      )
    )
  );

  @Effect()
  LoadJobAssignmentsOnSaveSuccess$: Observable<Action> = this.actions$.pipe(
    ofType<jobsActions.SaveJobSuccess>(
      jobsActions.JobsActionTypes.SaveJobSuccess
    ),
    withLatestFrom(this.store),
    map(([action, storeState]) =>
      storeState.jobs.selectedJobState.shouldReloadAssignments
        ? new jobsActions.LoadJobAssignments({
            jobId: action.payload.job.id,
          })
        : new jobsActions.Empty()
    )
  );

  @Effect()
  UpdateJobAssignment$: Observable<Action> = this.actions$.pipe(
    ofType<jobsActions.UpdateJobAssignment>(
      jobsActions.JobsActionTypes.UpdateJobAssignment
    ),
    mergeMap(action =>
      this.jobsService.updateJobAssignment(action.payload.assignment).pipe(
        map(
          (response: Assignment) =>
            new jobsActions.UpdateJobAssignmentSuccess({
              assignment: response,
            })
        ),
        catchError(errors => of(new jobsActions.LoadingJobsFailed({ errors })))
      )
    )
  );

  @Effect()
  saveCurrentJobState$ = this.actions$.pipe(
    ofType<jobsActions.SaveCurrentJobState>(
      jobsActions.JobsActionTypes.SaveCurrentJobState
    ),
    withLatestFrom(this.store),
    map(
      ([action, storeState]) =>
        new jobsActions.SaveJob({
          job: storeState.jobs.selectedJobState.job,
          listing: storeState.jobs.selectedJobState.listing,
          routeChange: true,
          showUpdateNotification: true,
        })
    )
  );

  @Effect()
  SaveJob$: Observable<Action> = this.actions$.pipe(
    ofType<jobsActions.SaveJob>(jobsActions.JobsActionTypes.SaveJob),
    switchMap(action =>
      this.jobsService.updateJob(action.payload.job).pipe(
        mergeMap((response: Job) => {
          const timeNow = new Date();
          return action.payload.job.id
            ? [
                new jobsActions.SaveJobSuccess({
                  job: response,
                  isNew: false,
                  showUpdateNotification: action.payload.showUpdateNotification,
                  lastUpdated: timeNow,
                }),
                new processActions.UpdateJobProcess({
                  jobId: response.id,
                }),
                new listingActions.SaveListing({
                  listing: action.payload.listing,
                  jobId: response.id,
                }),
                action.payload.publish
                  ? new jobsActions.PublishJob({
                      jobId: response.id,
                    })
                  : new jobsActions.Empty(),
              ]
            : [
                new jobsActions.SaveJobSuccess({
                  job: response,
                  isNew: true,
                  lastUpdated: timeNow,
                }),
                new processActions.CreateJobProcess({
                  jobId: response.id,
                }),
                new listingActions.SaveListing({
                  listing: action.payload.listing,
                  jobId: response.id,
                }),
              ];
        }),
        tap((r: any) => {
          if (r.payload.isNew) {
            this._trackNewJobPosting(action.payload.job);
            this._router.navigate(['dashboard/jobs', r.payload.job.id]);
          }
          if (action.payload.showUpdateNotification) {
            this._translate
              .get('job_snackbar_job_update_success')
              .pipe(take(1))
              .subscribe(text => {
                this._snackBar.open(text, null, {
                  duration: 3000,
                });
              });
          }
        }),
        catchError(errors => of(new jobsActions.LoadingJobsFailed({ errors })))
      )
    )
  );

  @Effect()
  DeleteJob$: Observable<Action> = this.actions$.pipe(
    ofType<jobsActions.DeleteJob>(jobsActions.JobsActionTypes.DeleteJob),
    switchMap(action =>
      this.jobsService.deleteJob(action.payload.jobId).pipe(
        map((response: Job) => new jobsActions.DeleteJobSuccess()),
        tap((r: any) => {
          this._router.navigate(['dashboard/overview']);
        }),
        catchError(errors => of(new jobsActions.LoadingJobsFailed({ errors })))
      )
    )
  );

  @Effect()
  PublishJob$: Observable<Action> = this.actions$.pipe(
    ofType<jobsActions.PublishJob>(jobsActions.JobsActionTypes.PublishJob),
    switchMap(action =>
      this.jobsService.publishJob(action.payload.jobId).pipe(
        map(
          (response: Job) =>
            new jobsActions.PublishJobSuccess({ job: response })
        ),
        tap((r: any) => {
          this._translate
            .get('post_job_alert_published_created_from_template_text')
            .pipe(take(1))
            .subscribe(text => {
              this._snackBar.open(text, null, {
                duration: 3000,
              });
            });
          this._router.navigate(['dashboard/job', r.payload.job.id]);
        }),
        catchError(errors =>
          of(new jobsActions.JobActionFail({ error: errors }))
        )
      )
    )
  );

  @Effect()
  UnpublishJob$: Observable<Action> = this.actions$.pipe(
    ofType<jobsActions.UnpublishJob>(jobsActions.JobsActionTypes.UnpublishJob),
    switchMap(action =>
      this.jobsService.unpublishJob(action.payload.jobId).pipe(
        map(
          (response: Job) =>
            new jobsActions.UnpublishJobSuccess({ job: response })
        ),
        tap((r: any) => {
          this._translate
            .get('post_job_alert_unpublished')
            .pipe(take(1))
            .subscribe(text => {
              this._snackBar.open(text, null, {
                duration: 3000,
              });
            });
        }),
        catchError(errors =>
          of(new jobsActions.JobActionFail({ error: errors }))
        )
      )
    )
  );

  @Effect()
  CloneJob$: Observable<Action> = this.actions$.pipe(
    ofType<jobsActions.CloneJob>(jobsActions.JobsActionTypes.CloneJob),
    switchMap(action =>
      this.jobsService.cloneJob(action.payload.jobId).pipe(
        map(
          (response: Job) => new jobsActions.CloneJobSuccess({ job: response })
        ),
        tap((r: any) => {
          this._translate
            .get('post_job_alert_cloned')
            .pipe(take(1))
            .subscribe(text => {
              this._snackBar.open(text, null, {
                duration: 3000,
              });
            });
          this._router.navigate([`dashboard/jobs/${r.payload.job.id}`]);
        }),
        catchError(errors =>
          of(new jobsActions.JobActionFail({ error: errors }))
        )
      )
    )
  );

  @Effect()
  CloneTemplateAsJob$: Observable<Action> = this.actions$.pipe(
    ofType<jobsActions.CloneTemplateAsJob>(
      jobsActions.JobsActionTypes.CloneTemplateAsJob
    ),
    switchMap(action =>
      this.jobsService.cloneAsJob(action.payload.jobId).pipe(
        map(
          (response: Job) =>
            new jobsActions.CloneTemplateAsJobSuccess({
              job: response,
            })
        ),
        tap((r: any) => {
          this._translate
            .get('post_job_alert_cloned')
            .pipe(take(1))
            .subscribe(text => {
              this._snackBar.open(text, null, {
                duration: 3000,
              });
            });
          this._router.navigate([`dashboard/jobs/${r.payload.job.id}`]);
        }),
        catchError(errors =>
          of(new jobsActions.JobActionFail({ error: errors }))
        )
      )
    )
  );

  private _trackNewJobPosting(newJob: Job) {
    try {
      this._facebookService.trackFacebookAnalyticsEvent(
        'post_job_created_job',
        {
          from_template: newJob.is_template,
        }
      );
      // Tracks Intercom Event when creating a job - regardless of publishing
      const metadata = {
        stillingstittel: newJob.title,
        kategori: newJob.category || '',
        lønnstype: this._contentService.getSalaryType(newJob.salary_type).name,
        kontor: newJob.unit_id,
        adresse: newJob.address,
        publisert: newJob.is_published,
        videointervju: newJob.has_video_interview || '',
      };
      this._intercomService.trackEvent('Antall jobber lagt ut', metadata);
    } catch (e) {
      console.log(e);
    }
  }

  constructor(
    private actions$: Actions,
    private jobsService: JobsService,
    private store: Store<fromRoot.State>,
    private _jobPostCanDeactivateGuard: JobPostCanDeactivateGuard,
    private _facebookService: FacebookService,
    private _contentService: ContentService,
    private _intercomService: IntercomService,
    private _translate: TranslateService,
    private _snackBar: MatSnackBar,
    private _router: Router
  ) {}
}
